import {
    Component,
    Element,
    Event,
    EventEmitter,
    Host,
    h,
    Prop,
    State,
    Watch,
} from "@stencil/core";
import { track } from "../../../utils/tracking";

/**
 * This component represents a tile in the `<ux-iws-nb-all-in-one>` module.
 * It is used to display a summary and allow toggling the corresponding detail section.
 *
 * @slot image - Slot for the image (`<picture>` or `<img>`)
 * @slot title - Slot for the headline (`<h2>` or `<h3>`)
 * @slot (default) - Slot for the text content
 */
@Component({
    tag: "ux-iws-nb-all-in-one-tile",
    styleUrl: "nb-all-in-one-tile.scss",
    shadow: false,
})
export class NbAllInOneTile {
    private static uidIncrement = 0;
    private uid = `nb-all-in-one-tile-${NbAllInOneTile.uidIncrement++}`;

    /** The element reference for the component. */
    @Element() host: HTMLUxIwsNbAllInOneTileElement;

    /** The related group identifier */
    @Prop({ reflect: true }) group: string = "";

    /** The tracking ID for analytics purposes */
    @Prop({ reflect: true }) trackingId: string = "";

    /** Fallback label if no title slot is provided */
    @Prop() label: string = "";

    /** Label for the CTA button if the tile is inactive */
    @Prop() activateLabel: string = "Show details";

    /** Label for the CTA button if the tile is active */
    @Prop() deactivateLabel: string = "Hide details";

    /** Indicates if the tile is active or not */
    @Prop({ mutable: true, reflect: true }) active: boolean = false;

    /** ID of the corresponding tab panel */
    @State() private tabPanelId: string | undefined = undefined;

    /**
     * Returns all tile elements within the parent container.
     * @returns An array of tile elements.
     */
    private getAllTiles = (): HTMLUxIwsNbAllInOneTileElement[] => {
        return Array.from(
            this.host.parentElement.querySelectorAll<HTMLUxIwsNbAllInOneTileElement>(
                "ux-iws-nb-all-in-one-tile"
            )
        );
    };

    /**
     * Event that is emitted when the show details button is clicked.
     */
    @Event({
        eventName: "uxShow",
        cancelable: true,
        composed: true,
        bubbles: true,
    })
    showEvent: EventEmitter<string>;

    /**
     * Event that is emitted when the hide details button is clicked.
     */
    @Event({
        eventName: "uxHide",
        cancelable: false,
        composed: true,
        bubbles: true,
    })
    hideEvent: EventEmitter<string>;

    /**
     * Watches for changes to the 'active' property and ensures only this tile is active among its siblings.
     * @param active - Indicates if this tile should be active.
     */
    @Watch("active")
    handleActiveChange(active: boolean) {
        if (!active) {
            return;
        }
        this.getAllTiles().forEach((el) => (el.active = el === this.host));
    }

    /**
     * Handles click events on the tile to toggle its active state and emit the appropriate event.
     */
    private handleClick = () => {
        if (this.active) {
            this.hideEvent.emit(this.group);
            this.active = false;
            return;
        }
        if (this.showEvent.emit(this.group).defaultPrevented) {
            return;
        }
        this.active = true;
        track("click_all_in_one_tile", {
            tile: this.trackingId,
        });
    };

    componentDidLoad() {
        window.setTimeout(() => {
            const groupElement = this.host.parentElement.querySelector(
                `ux-iws-nb-all-in-one-detail-group[group="${this.group}"]`
            );
            if (groupElement && groupElement.id) {
                this.tabPanelId = groupElement.id;
            }
        }, 0);
    }

    render() {
        const baseClass = "ux-iws-nb-all-in-one-tile";
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--active`]: this.active,
                }}
                role="tab"
                id={this.uid}
                aria-labelledby={`${this.uid}-title`}
                aria-controls={this.tabPanelId}
                aria-selected={this.active ? "true" : "false"}
                onClick={this.handleClick}
                tabindex={0}
            >
                <div class={`${baseClass}__gradient`} role="presentation" />
                <div class={`${baseClass}__image`} role="presentation">
                    <slot name="image" />
                </div>
                <div class={`${baseClass}__inner`}>
                    <div class={`${baseClass}__icon`}>
                        <ux-icon
                            name="plus"
                            weight="light"
                            aria-hidden="true"
                        />
                    </div>
                    <div class={`${baseClass}__content`}>
                        <div
                            class={`${baseClass}__title`}
                            id={`${this.uid}-title`}
                        >
                            <slot name="title">{this.label}</slot>
                        </div>
                        <div
                            class={`${baseClass}__text`}
                            id={`${this.uid}-desc`}
                        >
                            <slot />
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
